/* eslint-disable no-continue */
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'store';
import { updateSectionComponent } from 'store/slices/section';
import { DataFocusViewHeader } from './components';
import SectionRender, { SectionRenderHandle } from './components/SectionRender';
import { Alert, Box, Button, Grid, Typography } from '@mui/material';
import { useRecordData } from 'views/RecordDetailsViewer/hooks';
import { useLazyQuery, useMutation } from '@apollo/client';
import { FieldConditional, FindFormSections, FindFormSectionsVariables, FormSectionField, FormSectionWithChildren } from './types';
import { QUERY_FIND_FORM_SECTIONS } from 'graphql/queries/dataFocusView';
import { FormProvider, useForm, UseFormReturn } from 'react-hook-form';
import {
    filterFieldsEnabledByRecordType,
    formatDataToForm,
    generateDataFocusViewPayload,
    generateFormSectionWithChilds,
    generateRequiredByCondition,
    getObjectDefinitionByFieldName,
    getObjectDefinitionIds
} from './utils';
import { splitHeaders } from 'ui-component/records/utils/headerHelpers';
// import { useCustomListValues } from 'hooks/useCustomListValues';
import Loader from 'ui-component/Loader';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { IFormatDataValues, LogReportType } from 'ui-component/RecordView/types';
import { FieldDataTypeEnum, IRecordFieldsList, IRecordHeaders } from 'ui-component/records/types';
import { QUERY_GET_OBJECT_PROPERTIES } from 'graphql/queries/customObjects';
import { FindObjectProperty, FindObjectPropertyVariables, ObjectProperty } from 'views/backoffice/CustomObjects/types';
import { openSnackbar } from 'store/slices/snackbar';
import { FlexibleArea } from 'views/DocumentViewer/components';
import { DocumentViewerProvider } from 'views/DocumentViewer/context/DocumentViewerProvider';
import { Skeleton } from 'views/RecordDetailsViewer/components';
import { getLayoutPositionHeight } from 'views/RecordDetailsViewer/utils';
import { LogReportContainer, LogReportPanel } from 'ui-component/RecordView/PropertiesPanel/LogReportPanel';
import { LayoutPositionEnum } from 'views/RecordDetailsViewer/types';
import { ActivateSaveButtonFrom, ButtonTypeLog, LogReportMode, PanelTitle } from 'ui-component/RecordView/PropertiesPanel/utils';
import { LineItemsGrid } from 'ui-component/records/LineItems';
import { Attachments, CommentsPanel, RecordViewDialog } from 'ui-component/RecordView';
import SideBarOptions from 'ui-component/RecordView/PropertiesPanel/SideBar';
import { getAttachmentFields, getAttachmentFieldIds } from 'ui-component/RecordView/Attachments/utils';
import { filterNotAllowedFields } from 'ui-component/records/utils';
import useMyRole from 'hooks/useMyRole';
import { useUpdateRecord } from './hooks/useUpdateRecord';
import { RobotOops } from 'ui-component/icons/RobotOops';
import useUser from 'hooks/useUser';
import { MUTATION_UPLOAD_FILE } from 'graphql/mutations/records';
import { IUploadPdf, UploadPdfVariables } from 'views/CreateRecord/types';
import RightHandResizerContainer from 'ui-component/RightHandResizerContainer';
import { useConfirmationModalContext } from 'hooks/useConfirmationModal';
import { emptyRequiredFieldsErrorMessage } from 'views/DocumentViewer/utils';
import { useSelectedAndDefaultListTypesValues } from 'hooks/useSelectedAndDefaultListTypesValues';

export const DATA_FOCUS_VIEW_CORE_TOOLS = [
    'LINE_ITEMS',
    'COMMENTS',
    'ATTACHMENTS',
    'LOG_REPORT',
    'ENTITIES',
    'BUTTONS',
    'PDF_VIEW'
] as const;

export type DataFocusViewCoreTools = (typeof DATA_FOCUS_VIEW_CORE_TOOLS)[number];

export const DATA_FOCUS_VIEW_CORE_DEFAULT_COMPONENTS: Record<DataFocusViewCoreTools, boolean> = {
    LINE_ITEMS: true,
    COMMENTS: true,
    ATTACHMENTS: true,
    LOG_REPORT: true,
    ENTITIES: true,
    BUTTONS: true,
    PDF_VIEW: true
};

export type DataFocusViewCoreProps = {
    recordId: string;
    recordTypeId: string;
    components?: Partial<Record<DataFocusViewCoreTools, boolean>>;
    removeNotification?: boolean | 'success' | 'error';
};

export type DataFocusViewCoreRef = {
    handleSubmit: () => Promise<IRecordHeaders | boolean>;
    methods: UseFormReturn<any>;
};

export const DataFocusViewCore = forwardRef(
    (
        {
            recordId,
            recordTypeId,
            components = DATA_FOCUS_VIEW_CORE_DEFAULT_COMPONENTS,
            removeNotification = false
        }: DataFocusViewCoreProps,
        ref
    ) => {
        const dispatch = useDispatch();
        const roleId = useMyRole();
        const { userFromToken } = useUser();
        const modal = useConfirmationModalContext();
        const selectedDataFocusView = useSelector((store) => store.dataFocusView.selected);
        const { drawerOpen } = useSelector((store) => store.menu);
        const tenant = Number(localStorage.getItem('tenant_id'));
        const [isReseted, setIsReseted] = useState(false);
        // const [isTriggered, setIsTriggered] = useState(false);

        const [layoutPosition, setLayoutPosition] = useState<LayoutPositionEnum>('ONLY_DATA');
        const [isOpenLogReport, setIsOpenLogReport] = useState(false);
        const [isOpenPdfViewer, setIsOpenPdfViewer] = useState(false);
        const [logReportMode, setLogReportMode] = useState<LogReportMode>(LogReportMode.All);
        const [showFullHistory, setShowFullHistory] = useState(false);
        const [changePanel, setChangePanel] = useState<PanelTitle>(PanelTitle.Edit);
        const [buttonTypeLog, setButtonTypeLog] = useState<LogReportType>(ButtonTypeLog.Detail);

        const [showLineItemsPanel, setShowLogLineItemsPanel] = useState(false);
        const [showLogCommentsPanel, setShowLogCommentsPanel] = useState(false);
        const [showLogAttachmentsPanel, setShowLogAttachmentsPanel] = useState(false);
        const [rightHandPanelAndDrawerWidth, setRightHandPanelAndDrawerWidth] = useState(0);

        const [uploadFile] = useMutation<IUploadPdf, UploadPdfVariables>(MUTATION_UPLOAD_FILE);
        const [requiredFields, setRequiredFields] = useState<Array<{ sectionName: string; field: string }>>([]);
        const inputRefs = useRef<Record<string, HTMLDivElement | null>>({});
        const sectionRefs = useRef<Record<string, React.RefObject<SectionRenderHandle>>>({});

        const {
            data: recordData,
            headers,
            headersWithFieldName,
            allowedFieldHeaders,
            fieldAccess: fieldAccessList,
            loading: loadingRecordData
        } = useRecordData({ recordId: +recordId, recordTypeId: +recordTypeId });

        const { isLoading: loadingListValues, listValuesByListId } = useSelectedAndDefaultListTypesValues(headers, recordData || undefined);

        const checkAllowedFields = useMemo(() => filterNotAllowedFields(fieldAccessList), [fieldAccessList]);
        const { additionalFieldHeaders } = useMemo(() => splitHeaders(headers || null, checkAllowedFields), [headers, checkAllowedFields]);

        const isAttachmentFieldAllowed = Object.values(additionalFieldHeaders).filter((value) => value?.dataType === 'attachment');

        const attachmentFieldIds = useMemo(() => getAttachmentFieldIds(headers), [headers]);
        const attachmentsData = useMemo(
            () => getAttachmentFields(attachmentFieldIds, recordData?.additionalFields),
            [attachmentFieldIds, recordData?.additionalFields]
        );
        const [getObjectProperties, { data: propertiesData }] = useLazyQuery<FindObjectProperty, FindObjectPropertyVariables>(
            QUERY_GET_OBJECT_PROPERTIES
        );

        const [findFormSections, { data: formSectionsData, loading: loadingFormSections }] = useLazyQuery<
            FindFormSections,
            FindFormSectionsVariables
        >(QUERY_FIND_FORM_SECTIONS, {
            fetchPolicy: 'no-cache',
            onCompleted(data) {
                if (data.findFormSectionsMany.length) {
                    const allFields = data.findFormSectionsMany.reduce<FormSectionField[]>(
                        (acc, { sectionFields }) => [...acc, ...sectionFields],
                        []
                    );
                    const allObjectPropertyIds = allFields
                        .filter(({ objectProperty }) => objectProperty)
                        .map(({ objectProperty }) => +objectProperty.id);

                    getObjectProperties({ variables: { data: { ids: allObjectPropertyIds } } });
                }
            }
        });

        const { updateRecord, updatingRecord } = useUpdateRecord(recordId, recordTypeId, 'FINDRECORDHEADERWITHFILTERING');

        const formSections: FormSectionWithChildren[] = useMemo(
            () => generateFormSectionWithChilds(formSectionsData?.findFormSectionsMany || []),
            [formSectionsData?.findFormSectionsMany]
        );

        const renderConditions: FieldConditional[] = useMemo(
            () =>
                formSections
                    .filter((section) => section.renderCondition)
                    .flatMap((section) => {
                        try {
                            const conditions = JSON.parse(section.renderCondition || '[]');
                            return Array.isArray(conditions) ? conditions : [];
                        } catch (error) {
                            console.error('Error parsing renderCondition:', error);
                            return [];
                        }
                    }),
            [formSections]
        );

        const renderConditionResultsBySectionId = !renderConditions
            ? undefined
            : renderConditions.reduce<Record<string, FieldConditional[]>>((acc, el) => {
                  const usedSectionIds = [...el.then.map(({ sectionId }) => sectionId), el.otherwise.sectionId];

                  const updatedSectionIds: Record<string, FieldConditional[]> = {};

                  for (const sectionId of usedSectionIds) {
                      if (acc[sectionId]) updatedSectionIds[sectionId] = [...acc[sectionId], el];
                      else updatedSectionIds[sectionId] = [el];
                  }
                  return { ...acc, ...updatedSectionIds };
              }, {});

        const objectPropertiesById =
            propertiesData?.findObjectProperty.reduce<Record<number, ObjectProperty>>((acc, el) => ({ ...acc, [+el.id]: el }), {}) || {};
        const objectDefinitionIds = useMemo(() => getObjectDefinitionIds(headers), [headers]);
        const objectPropertiesByFieldName = useMemo(
            () => getObjectDefinitionByFieldName(propertiesData?.findObjectProperty, objectDefinitionIds),
            [objectDefinitionIds, propertiesData?.findObjectProperty]
        );

        const getSchema = () => {
            if (formSections.length > 0) {
                const allFields = formSections.reduce<FormSectionField[]>(
                    (acc, { sectionFields }) => [
                        ...acc,
                        ...sectionFields.filter(filterFieldsEnabledByRecordType(headersWithFieldName || {}, objectPropertiesByFieldName))
                    ],
                    []
                );

                const sectionBaseFields = allFields.filter(({ baseField }) => !!baseField);
                const sectionAdditionalFields = allFields.filter(({ recordAdditionalFieldsByType }) => !!recordAdditionalFieldsByType);
                const sectionObjectPropertyFields = allFields.filter(({ objectProperty }) => !!objectProperty);

                const requiredBySectionNonObjectPropertyFieldsName = allFields
                    .filter((el) => el.isRequired && (el.baseField || el.recordAdditionalFieldsByType?.name))
                    .map((el) => el.baseField || el.recordAdditionalFieldsByType.name);

                const requiredFieldObj: Record<string, any> = {};

                for (const name of requiredBySectionNonObjectPropertyFieldsName) requiredFieldObj[name] = yup.string().trim().required('');

                for (const field of sectionBaseFields) {
                    const fieldName = field.baseField;
                    const fieldDef = headers?.[fieldName];
                    const isDropdown = fieldDef?.dataType === 'dropDown' || fieldDef?.dataType === 'dropdown';
                    if (fieldDef?.required || field.isRequired) {
                        if (isDropdown) requiredFieldObj[fieldName] = yup.number().min(1).required('');
                        else requiredFieldObj[fieldName] = yup.string().trim().required('');
                    }
                }

                for (const field of sectionAdditionalFields) {
                    const fieldName = field.recordAdditionalFieldsByType.name;
                    const fieldDef = headers?.[fieldName];
                    const isDropdown =
                        fieldDef && (['dropDown', 'dropdown', 'multiselect'] as Partial<FieldDataTypeEnum>[]).includes(fieldDef.dataType);
                    if (fieldDef?.required || field.isRequired) {
                        if (isDropdown)
                            requiredFieldObj[fieldName] = yup
                                .mixed()
                                .test('es-string-valido', '', (value) => typeof value !== 'string' || (!!value && value !== '0'))
                                .test('es-numero-valido', '', (value) => typeof value !== 'number' || value !== 0)
                                .required();
                        else requiredFieldObj[fieldName] = yup.string().trim().required('');
                    }
                    if (fieldDef?.dataType === 'email') {
                        requiredFieldObj[fieldName] = yup
                            .string()
                            .trim()
                            .email('Invalid format. Please enter a valid email address (e.g. example@domain.com).');
                    }
                }

                const objectsToSchema: Record<string, string[]> = {};

                for (const field of sectionObjectPropertyFields) {
                    // debugger;
                    const propertyDef = objectPropertiesById[+field.objectProperty.id];
                    if (propertyDef) {
                        const objectName = propertyDef?.objectDefinition?.name || '';
                        if (propertyDef?.isRequired || field.isRequired) {
                            const itExist = objectsToSchema[objectName];
                            if (itExist) objectsToSchema[objectName].push(propertyDef.name);
                            else objectsToSchema[objectName] = [propertyDef.name];
                        }
                    }
                }

                for (const objectName in objectsToSchema) {
                    if (Object.prototype.hasOwnProperty.call(objectsToSchema, objectName)) {
                        const propertiesAsSchema = objectsToSchema[objectName].reduce<Record<string, any>>(
                            (acc, el) => ({ ...acc, [el]: yup.string().trim().required('') }),
                            {}
                        );
                        requiredFieldObj[objectName] = yup.object(propertiesAsSchema);
                    }
                }

                return requiredFieldObj;
            }

            return {};
        };

        const [otherRequiredSchema, setOtherRequiredSchema] = useState({});

        const methods = useForm({
            mode: 'onChange',
            resolver: yupResolver(yup.object({ ...getSchema(), ...otherRequiredSchema }).required())
        });

        const [currentValues /* setCurrentValues */] = useState<Record<string, any>>({});

        const fulfilledConditionIds = useMemo(
            () =>
                renderConditions?.reduce<string[]>((acc, el) => {
                    const fullfilledArr = [];
                    if (el.linkOperator === 'AND') {
                        let fullfilledAll = true;
                        for (const condition of el.condition) {
                            const { field, value, operator, objectProperty } = condition;
                            const fieldDef = headersWithFieldName?.[field];
                            if (fieldDef) {
                                const fieldValue = objectProperty ? currentValues[field]?.[objectProperty] : currentValues[field];

                                if (fieldValue === undefined) {
                                    fullfilledAll = false;
                                    break;
                                }

                                if (operator === 'CONTAINS' && fieldValue.includes(value)) {
                                    continue;
                                }

                                if (operator === 'DOES_NOT_CONTAINS' && !fieldValue.includes(value)) {
                                    continue;
                                }

                                if (operator === 'IS_EQUAL_TO' && String(fieldValue) === String(value)) {
                                    continue;
                                }

                                if (operator === 'IS_FALSE' && String(fieldValue) === 'false') {
                                    continue;
                                }

                                if (operator === 'IS_GREATER_OR_EQUAL_TO' && Number(fieldValue) >= Number(value)) {
                                    continue;
                                }

                                if (operator === 'IS_GREATER_THAN' && Number(fieldValue) > Number(value)) {
                                    continue;
                                }

                                if (operator === 'IS_LOWER_OR_EQUAL_TO' && Number(fieldValue) <= Number(value)) {
                                    continue;
                                }

                                if (operator === 'IS_LOWER_THAN' && Number(fieldValue) < Number(value)) {
                                    continue;
                                }

                                if (fieldValue && operator === 'IS_NOT_EMPTY') {
                                    continue;
                                }

                                if (operator === 'IS_NOT_EQUAL_TO' && String(fieldValue) !== String(value)) {
                                    continue;
                                }

                                if (operator === 'IS_TRUE' && String(fieldValue) === 'true') {
                                    continue;
                                }

                                fullfilledAll = false;
                                break;
                            } else {
                                fullfilledAll = false;
                                break;
                            }
                        }

                        if (fullfilledAll) fullfilledArr.push(el.id);
                    } else {
                        let fulfilledAny = false;
                        for (const condition of el.condition) {
                            const { field, value, operator } = condition;
                            const fieldDef = headersWithFieldName?.[field];
                            if (fieldDef) {
                                const fieldValue = currentValues[field];

                                if (operator === 'CONTAINS' && fieldValue.includes(value)) {
                                    continue;
                                }

                                if (operator === 'DOES_NOT_CONTAINS' && !fieldValue.includes(value)) {
                                    continue;
                                }

                                if (operator === 'IS_EQUAL_TO' && String(fieldValue) === String(value)) {
                                    continue;
                                }

                                if (operator === 'IS_FALSE' && String(fieldValue) === 'false') {
                                    continue;
                                }

                                if (operator === 'IS_GREATER_OR_EQUAL_TO' && Number(fieldValue) >= Number(value)) {
                                    continue;
                                }

                                if (operator === 'IS_GREATER_THAN' && Number(fieldValue) > Number(value)) {
                                    continue;
                                }

                                if (operator === 'IS_LOWER_OR_EQUAL_TO' && Number(fieldValue) <= Number(value)) {
                                    continue;
                                }

                                if (operator === 'IS_LOWER_THAN' && Number(fieldValue) < Number(value)) {
                                    continue;
                                }

                                if (fieldValue && operator === 'IS_NOT_EMPTY') {
                                    continue;
                                }

                                if (operator === 'IS_NOT_EQUAL_TO' && String(fieldValue) !== String(value)) {
                                    continue;
                                }

                                if (operator === 'IS_TRUE' && String(fieldValue) === 'true') {
                                    continue;
                                }

                                fulfilledAny = true;
                                break;
                            }
                        }
                        if (fulfilledAny) fullfilledArr.push(el.id);
                    }

                    return [...acc, ...fullfilledArr];
                }, []) || [],
            [renderConditions, headersWithFieldName, currentValues]
        );

        useEffect(() => {
            methods.trigger();
        }, [otherRequiredSchema, methods]);

        useEffect(() => {
            const additionalSchema = generateRequiredByCondition(renderConditions, fulfilledConditionIds, headers);
            setOtherRequiredSchema(additionalSchema);
        }, [fulfilledConditionIds, renderConditions, headers]);

        const handleToggleLogReport = () => {
            setIsOpenLogReport((value) => !value);
            setChangePanel((s) => (s === PanelTitle.LogReport ? PanelTitle.Edit : PanelTitle.LogReport));
            setLogReportMode(LogReportMode.All);
            if (changePanel === PanelTitle.LogReport) {
                setShowLogCommentsPanel?.(false);
                setShowLogLineItemsPanel?.(false);
                setShowLogAttachmentsPanel?.(false);
            }
        };

        const handleToggleComments = () => {
            setLayoutPosition(layoutPosition === 'SPLIT_COMMENTS' ? 'ONLY_DATA' : 'SPLIT_COMMENTS');
            if (components.LOG_REPORT)
                setLogReportMode(LogReportMode.Comments === logReportMode ? LogReportMode.All : LogReportMode.Comments);
        };

        const handleToggleLineItems = () => {
            setLayoutPosition(layoutPosition === 'SPLIT_LINE_ITEMS' ? 'ONLY_DATA' : 'SPLIT_LINE_ITEMS');
            if (components.LOG_REPORT)
                setLogReportMode(LogReportMode.LineItems === logReportMode ? LogReportMode.All : LogReportMode.LineItems);
        };

        const handleToggleAttachments = () => {
            setLayoutPosition(layoutPosition === 'SPLIT_ATTACHMENTS' ? 'ONLY_DATA' : 'SPLIT_ATTACHMENTS');
            if (components.LOG_REPORT)
                setLogReportMode(LogReportMode.Attachments === logReportMode ? LogReportMode.All : LogReportMode.Attachments);
        };

        const handleClickSubmit = async () => {
            const sortedSections = [...formSections].sort((a, b) => (a.order || 0) - (b.order || 0));
            const formData = methods.getValues();
            const emptyRequiredFields = (requiredFields || []).filter((field: { sectionName: string; field: string }) => {
                const value = formData[field.field];
                return !value || value === -1;
            });

            if (emptyRequiredFields.length > 0) {
                emptyRequiredFields.forEach((item: { sectionName: string; field: string }) => {
                    methods.setError(item.field, {
                        type: 'manual',
                        message: emptyRequiredFieldsErrorMessage(headersWithFieldName?.[item.field]?.dataType || '')
                    });
                });

                let firstErrorField = null;
                let targetSection = null;

                for (const section of sortedSections) {
                    const errorFieldInSection = emptyRequiredFields.find((fieldObj) => fieldObj.sectionName === section.name);

                    if (errorFieldInSection) {
                        firstErrorField = errorFieldInSection.field;
                        targetSection = section;
                        break;
                    }
                }

                if (targetSection && firstErrorField) {
                    const sectionRef = sectionRefs.current[targetSection.id];
                    if (sectionRef && sectionRef.current) {
                        sectionRef.current.scrollSectionIntoView();
                        sectionRef.current.scrollToField(firstErrorField);
                    }
                } else {
                    if (!firstErrorField) {
                        firstErrorField = emptyRequiredFields[0].field;
                    }

                    const node = inputRefs.current[firstErrorField];
                    if (node && typeof node.scrollIntoView === 'function') {
                        node.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }
                }
            } else {
                await modal.showConfirmation({
                    title: 'Confirm Submission?',
                    content: (
                        <Alert
                            variant="filled"
                            icon={false}
                            sx={{
                                bgcolor: '#FB9000',
                                color: 'white',
                                borderRadius: '8px !important',
                                height: '32px',
                                fontSize: '14px',
                                width: 'fit-content',
                                padding: '0 16px !important',
                                fontWeight: 500
                            }}
                        >
                            This action is irreversible. Once submitted it cannot be edited.
                        </Alert>
                    ),
                    forwardButtonText: 'Confirm',
                    actionButtonText: 'Cancel',
                    actionType: 'submit',
                    showCheckbox: true,
                    onAction: async () => {},
                    onForward: async () => {
                        await handleSubmit();
                    },
                    recordType: 'purchase_order',
                    sectionTitle: 'portal_request'
                });
            }
        };

        const handleSubmit = async () => {
            const data = methods.getValues();
            const editableFieldKeys = fieldAccessList
                ?.filter((access) => access.access.toLowerCase() === 'editable')
                .map((field) => field.recordFieldName);
            const editableHeaders = Object.fromEntries(
                Object.entries(allowedFieldHeaders || {}).filter(([key]) => editableFieldKeys.includes(key))
            );

            let newFileId;
            if (data.recordFileId instanceof File) {
                const res = await uploadFile({
                    variables: {
                        file: data.recordFileId
                    }
                });
                newFileId = res.data?.uploadRecordFile.id || null;
            } else {
                newFileId = data.recordFileId;
            }

            const payload = await generateDataFocusViewPayload(
                editableHeaders as IRecordFieldsList,
                objectPropertiesByFieldName,
                data,
                userFromToken,
                fieldAccessList
            );

            try {
                const res = await updateRecord({
                    variables: {
                        data: {
                            ...payload,
                            id: +recordId,
                            recordType: +recordTypeId,
                            enabled: true,
                            tenant,
                            recordFileId: newFileId ? Number(newFileId) : newFileId
                        }
                    }
                });
                if (!removeNotification || removeNotification !== 'success')
                    dispatch(
                        openSnackbar({
                            open: true,
                            message: `Update successful`,
                            variant: 'alert',
                            alert: { color: 'success' },
                            close: true
                        })
                    );
                return res.data?.updateRecordHeader || false;
            } catch (error: any) {
                console.log('error updating the record', error);
                if (!removeNotification || removeNotification !== 'error')
                    dispatch(
                        openSnackbar({
                            open: true,
                            message: `Save changes failed: ${error.message || 'Internal server error.'}`,
                            variant: 'alert',
                            alert: { color: 'error' },
                            close: true
                        })
                    );
            }
            return false;
        };

        const handleOnSubmitFile = async (_recordType: number, data: IFormatDataValues, showAlerts: boolean = true) => {
            const { baseData, additionalFields } = data;
            let newFileId;
            const formattedData = {
                ...baseData,
                id: Number(recordId),
                additionalFields
            };

            try {
                const res = await updateRecord({
                    variables: { data: { ...formattedData, recordFileId: Number(newFileId) || undefined } }
                });
                if (showAlerts && (!removeNotification || removeNotification !== 'success'))
                    dispatch(
                        openSnackbar({
                            open: true,
                            message: `Update successful`,
                            variant: 'alert',
                            alert: {
                                color: 'success'
                            },
                            close: true
                        })
                    );
                return res.data?.updateRecordHeader || false;
            } catch (err: any) {
                console.log(err);
                if (showAlerts && (!removeNotification || removeNotification !== 'error'))
                    dispatch(
                        openSnackbar({
                            open: true,
                            message: `Save changes failed: ${err.message || 'Internal server error.'}`,
                            variant: 'alert',
                            alert: {
                                color: 'error'
                            },
                            close: true
                        })
                    );
                return false;
            }
        };

        useImperativeHandle(ref, () => ({
            handleSubmit,
            methods
        }));

        useEffect(() => {
            if (!recordData) return;
            const data = formatDataToForm(allowedFieldHeaders as IRecordFieldsList, recordData);
            methods.reset(data);
            setIsReseted(true);
        }, [allowedFieldHeaders, methods, recordData, selectedDataFocusView]);

        useEffect(() => {
            if (!loadingFormSections && isReseted) {
                setTimeout(() => {
                    methods.trigger();
                    // setIsTriggered(true);
                }, 1000);
            }
        }, [isReseted, selectedDataFocusView, loadingFormSections, methods]);

        useEffect(() => {
            if (selectedDataFocusView) {
                setRequiredFields([]);
                findFormSections({ variables: { data: { formId: +selectedDataFocusView.form.id } } });
            }
        }, [findFormSections, selectedDataFocusView]);

        useEffect(() => {
            dispatch(updateSectionComponent(<DataFocusViewHeader recordTypeId={recordTypeId} />));
        }, [dispatch, recordTypeId]);

        useEffect(() => {
            if (showLogCommentsPanel) {
                setIsOpenLogReport(true);
                setChangePanel(PanelTitle.LogReport);
                setLogReportMode(LogReportMode.Comments);
                setShowLogLineItemsPanel?.(false);
                setShowLogAttachmentsPanel?.(false);
            } else if (showLineItemsPanel) {
                setIsOpenLogReport(true);
                setChangePanel(PanelTitle.LogReport);
                setLogReportMode(LogReportMode.LineItems);
                setShowLogCommentsPanel?.(false);
                setShowLogAttachmentsPanel?.(false);
            } else if (showLogAttachmentsPanel) {
                setIsOpenLogReport(true);
                setChangePanel(PanelTitle.LogReport);
                setLogReportMode(LogReportMode.Attachments);
                setShowLogCommentsPanel?.(false);
                setShowLogLineItemsPanel?.(false);
            } else {
                setIsOpenLogReport(false);
                setChangePanel(PanelTitle.Edit);
                setLogReportMode(LogReportMode.All);
                setShowLogCommentsPanel?.(false);
                setShowLogLineItemsPanel?.(false);
                setShowLogAttachmentsPanel?.(false);
            }
        }, [
            showLogCommentsPanel,
            showLineItemsPanel,
            showLogAttachmentsPanel,
            setShowLogLineItemsPanel,
            setShowLogCommentsPanel,
            setShowLogAttachmentsPanel
        ]);

        const dynamicTitleFooterButton = useMemo(() => {
            if (logReportMode === LogReportMode.Comments) {
                return 'Show full comments history';
            }

            if (logReportMode === LogReportMode.LineItems) {
                return 'Show full line items history';
            }

            return 'Show full history';
        }, [logReportMode]);

        if (!recordId || !recordTypeId) return <Navigate to="../dashboard" replace />;

        if (loadingRecordData || !headersWithFieldName || loadingFormSections) return <Loader />;

        if (!selectedDataFocusView && !loadingRecordData && headersWithFieldName && !loadingFormSections)
            return (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '20px',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%'
                    }}
                >
                    <RobotOops />
                    <Typography fontSize="24px">No forms found for this record. Contact your admin for help!</Typography>
                </Box>
            );

        return (
            <DocumentViewerProvider>
                <RightHandResizerContainer
                    drawerOpen={drawerOpen}
                    activateResize={isOpenLogReport}
                    initialWidth={isOpenLogReport ? -1 : 52}
                    onCenterWidthChange={(width) => {
                        setRightHandPanelAndDrawerWidth(width.rightPanelAndDrawerWidth);
                    }}
                    sxProps={{ width: '100%', '& > div:first-of-type': { width: 'calc(100% - 52px)' } }}
                    centerContent={
                        <>
                            {loadingListValues || loadingRecordData || !headersWithFieldName || loadingFormSections ? (
                                <FlexibleArea hideTitle height="100vh">
                                    <Skeleton />
                                </FlexibleArea>
                            ) : (
                                <Box display="flex" flexDirection="row" height="100%" justifyContent="space-between">
                                    <Box sx={{ width: '100%' }} height="100%">
                                        <FlexibleArea
                                            paperSx={{ overflowY: 'auto' }}
                                            hideTitle
                                            height={getLayoutPositionHeight('ONLY_DATA', layoutPosition)}
                                        >
                                            <FormProvider {...methods}>
                                                {formSections
                                                    .filter(
                                                        ({ sectionFields, specialComponent }) =>
                                                            specialComponent ||
                                                            sectionFields.filter(
                                                                filterFieldsEnabledByRecordType(
                                                                    headersWithFieldName,
                                                                    objectPropertiesByFieldName
                                                                )
                                                            ).length
                                                    )
                                                    .sort((a, b) => a.order - b.order)
                                                    .map((section) => {
                                                        if (!sectionRefs.current[section.id]) {
                                                            sectionRefs.current[section.id] = React.createRef<SectionRenderHandle>();
                                                        }
                                                        return (
                                                            <SectionRender
                                                                ref={sectionRefs.current[section.id]}
                                                                recordId={recordId}
                                                                recordData={recordData}
                                                                key={section.id}
                                                                section={section}
                                                                headers={headersWithFieldName}
                                                                renderConditions={renderConditionResultsBySectionId?.[section.id]}
                                                                fullfilledConditionsIds={fulfilledConditionIds}
                                                                recordTypeId={recordTypeId}
                                                                propertiesById={objectPropertiesById}
                                                                propertyByFieldName={objectPropertiesByFieldName}
                                                                fieldAccessList={fieldAccessList}
                                                                listValuesByListId={listValuesByListId}
                                                                activeDataFocusView
                                                                loadingListValues={loadingListValues}
                                                                forcedWidth={rightHandPanelAndDrawerWidth}
                                                                setRequiredFields={setRequiredFields}
                                                                inputRefs={inputRefs}
                                                            />
                                                        );
                                                    })}
                                            </FormProvider>
                                        </FlexibleArea>
                                        <FlexibleArea
                                            title="Comments"
                                            forcedWidth={rightHandPanelAndDrawerWidth}
                                            toggleFullHeight={() =>
                                                setLayoutPosition(layoutPosition === 'SPLIT_COMMENTS' ? 'ONLY_COMMENTS' : 'SPLIT_COMMENTS')
                                            }
                                            setToggleHistoryPanel={setShowLogCommentsPanel}
                                            toggleHistoryPanel={showLogCommentsPanel}
                                            showHistoryPanel
                                            fullHeight={layoutPosition === 'ONLY_COMMENTS'}
                                            height={getLayoutPositionHeight('ONLY_COMMENTS', layoutPosition)}
                                            showDivider={layoutPosition === 'SPLIT_COMMENTS'}
                                            contentSx={{ paddingLeft: '24px', pt: 1 }}
                                            fullHeightButtonSx={{ mr: '15px' }}
                                            onClose={() => {
                                                setLayoutPosition('ONLY_DATA');
                                                setLogReportMode(LogReportMode.All);
                                            }}
                                        >
                                            {['ONLY_COMMENTS', 'SPLIT_COMMENTS'].includes(layoutPosition) && <CommentsPanel />}
                                        </FlexibleArea>
                                        <FlexibleArea
                                            title="Add Line Items"
                                            forcedWidth={rightHandPanelAndDrawerWidth}
                                            toggleFullHeight={() =>
                                                setLayoutPosition(
                                                    layoutPosition === 'SPLIT_LINE_ITEMS' ? 'ONLY_LINE_ITEMS' : 'SPLIT_LINE_ITEMS'
                                                )
                                            }
                                            setToggleHistoryPanel={setShowLogLineItemsPanel}
                                            toggleHistoryPanel={showLineItemsPanel}
                                            showHistoryPanel
                                            fullHeight={layoutPosition === 'ONLY_LINE_ITEMS'}
                                            height={getLayoutPositionHeight('ONLY_LINE_ITEMS', layoutPosition)}
                                            showDivider={layoutPosition === 'SPLIT_LINE_ITEMS'}
                                            contentSx={{ paddingLeft: '24px', pt: 1 }}
                                            fullHeightButtonSx={{ mr: '15px' }}
                                            onClose={() => {
                                                setLayoutPosition('ONLY_DATA');
                                                setLogReportMode(LogReportMode.All);
                                            }}
                                        >
                                            <LineItemsGrid
                                                isOpen={(['SPLIT_LINE_ITEMS', 'ONLY_LINE_ITEMS'] as LayoutPositionEnum[]).includes(
                                                    layoutPosition
                                                )}
                                                recordId={Number(recordId)}
                                                recordTypeId={Number(recordTypeId)}
                                                showCustomNoRowsOverlay={layoutPosition === 'ONLY_LINE_ITEMS'}
                                            />
                                        </FlexibleArea>
                                        <FlexibleArea
                                            title="Attachments"
                                            forcedWidth={rightHandPanelAndDrawerWidth}
                                            toggleFullHeight={() =>
                                                setLayoutPosition(
                                                    layoutPosition === 'SPLIT_ATTACHMENTS' ? 'ONLY_ATTACHMENTS' : 'SPLIT_ATTACHMENTS'
                                                )
                                            }
                                            setToggleHistoryPanel={setShowLogAttachmentsPanel}
                                            toggleHistoryPanel={showLogAttachmentsPanel}
                                            showHistoryPanel
                                            fullHeight={layoutPosition === 'ONLY_ATTACHMENTS'}
                                            height={getLayoutPositionHeight('ONLY_ATTACHMENTS', layoutPosition)}
                                            showDivider={layoutPosition === 'SPLIT_ATTACHMENTS'}
                                            contentSx={{ paddingLeft: '24px', pt: 1 }}
                                            onClose={() => {
                                                setLayoutPosition('ONLY_DATA');
                                                setLogReportMode(LogReportMode.All);
                                            }}
                                        >
                                            {['ONLY_ATTACHMENTS', 'SPLIT_ATTACHMENTS'].includes(layoutPosition) && (
                                                <Attachments
                                                    fullHeight={layoutPosition === 'ONLY_ATTACHMENTS'}
                                                    attachmentFieldIds={attachmentFieldIds}
                                                    attachmentsData={attachmentsData}
                                                    onUpdateAttachment={(data) => handleOnSubmitFile(Number(recordTypeId), data, false)}
                                                    recordTypeId={recordTypeId}
                                                    roleId={roleId}
                                                />
                                            )}
                                        </FlexibleArea>
                                    </Box>
                                </Box>
                            )}
                        </>
                    }
                    rightContent={
                        <Grid container flexDirection="column" justifyContent="space-between" height="100%" width="100%">
                            <Grid item xs={12} sx={{ maxHeight: '100%', '& #scrollable-list': { overflowY: 'auto', maxHeight: '100%' } }}>
                                {isOpenLogReport ? (
                                    <LogReportContainer>
                                        <LogReportPanel
                                            recordId={Number(recordData?.id)}
                                            showFullHistory={showFullHistory}
                                            onCloseFullHistory={() => setShowFullHistory(false)}
                                            logReportMode={logReportMode}
                                            onChangeSelection={setLogReportMode}
                                            buttonTypeLog={buttonTypeLog}
                                            setButtonTypeLog={setButtonTypeLog}
                                            toggleLogReport={handleToggleLogReport}
                                            striped
                                            showSearch
                                        />
                                        <Button
                                            variant="contained"
                                            disableElevation
                                            disableRipple
                                            sx={(theme) => ({
                                                mt: 'auto',
                                                maxWidth: 'fit-content',
                                                bgcolor: 'white',
                                                color: theme.palette.secondary.main,
                                                borderRadius: '8px',
                                                '&:hover': { bgcolor: 'white', color: theme.palette.secondary.main }
                                            })}
                                            onClick={() => setShowFullHistory(true)}
                                        >
                                            {dynamicTitleFooterButton}
                                        </Button>
                                    </LogReportContainer>
                                ) : (
                                    <SideBarOptions
                                        isCreate={false}
                                        changePanel={changePanel}
                                        recordDataId={Number(recordId)}
                                        recordTypeId={Number(recordTypeId)}
                                        toggleLogReport={components.LOG_REPORT ? handleToggleLogReport : undefined}
                                        onToggleLineItems={components.LINE_ITEMS ? handleToggleLineItems : undefined}
                                        onToggleComments={components.COMMENTS ? handleToggleComments : undefined}
                                        onToggleAttachments={components.ATTACHMENTS ? handleToggleAttachments : undefined}
                                        onToggleRecordView={components.PDF_VIEW ? () => setIsOpenPdfViewer(true) : undefined}
                                        hideButtons={!components.BUTTONS}
                                        logReportMode={logReportMode}
                                        isObjectPanel={false}
                                        loading={updatingRecord}
                                        isAttachmentFieldAllowed={!!isAttachmentFieldAllowed.length}
                                        handleClickSave={handleClickSubmit}
                                        businessEntity={recordData?.businessEntity ?? null}
                                        from={ActivateSaveButtonFrom.DataFocusView}
                                        onGoBack={() => {}}
                                        handleClickCancel={() => {}}
                                        setActivateRightHandPanel={() => {}}
                                        activateRightHandPanel={false}
                                    />
                                )}
                                {recordData && (
                                    <RecordViewDialog
                                        recordData={recordData}
                                        open={isOpenPdfViewer}
                                        onClose={(success?: boolean) => {
                                            if (success) {
                                                dispatch(
                                                    openSnackbar({
                                                        open: true,
                                                        message: `Record saved!`,
                                                        variant: 'alert',
                                                        alert: { color: 'success' },
                                                        close: true
                                                    })
                                                );
                                            }
                                            setIsOpenPdfViewer(false);
                                        }}
                                    />
                                )}
                            </Grid>
                        </Grid>
                    }
                />
            </DocumentViewerProvider>
        );
    }
);
